'use client';
import { useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

// Define the Link interface
export interface ILink {
  href: string;
  name?: string; // Optional for links without display names
  target?: string; // Optional for external links or links needing target="_blank"
}

// Define the Links structure
export interface ILinks {
  home: ILink;
  pricing: ILink;
  humanClonePage: ILink;
  videoChatbot: ILink;
  external: ILink;
  termsOfService: ILink;
  faq: ILink;
  contactUs: ILink;
  partnership: ILink;
  login: ILink;
  register: ILink;
  Roadmap: ILink;
  AIChatbot: ILink;
  ScenarioChatbot: ILink;
  VideoBot: ILink;
  HumanClone: ILink;
  CallBotPage: ILink;
  WhyVideoBot: ILink;
  TextVSVideo: ILink;
  ChatbotIntegration: ILink;
  BecomeAPartner: ILink;
  BecomeAnAffiliate: ILink;
  IntegrationStripe: ILink;
  SiteBot: ILink;
  Voicemail: ILink;
  Marketplace: ILink;
}

export default function useLinks(locale: string) {
  // Utilisez 'locale' au lieu de 'lang' pour correspondre à votre structure de route
  const { t } = useTranslation(locale);

  const Links = useMemo<ILinks>(() => {
    return {
      home: {
        href: `/${locale}`,
        name: t('SiteNavbar/l-05'),
      },
      pricing: {
        href: `/${locale}/plans`,
        name: t('SiteSocialNetwork/link-02'),
      },
      humanClonePage: {
        href: `/${locale}/human-clone-page`,
        name: t('SiteNavbar/l-06'),
      },
      videoChatbot: {
        href: `/${locale}/video-chatbot`,
        name: t('SiteNavbar/l-07'),
      },
      CallBotPage: {
        href: `/${locale}/callbot`,
        name: t('SiteNavbar/l-17'),
      },
      external: {
        href: `${process.env.NEXT_PUBLIC_DASHBOARD_BASE_URL as string}/${locale}/login`,
        name: t('SiteSocialNetwork/link-06'),
        target: '_blank',
      },
      termsOfService: {
        href: `/${locale}/terms-of-service`,
        name: t('SiteSocialNetwork/link-03'),
        target: '_blank',
      },
      faq: {
        href: `/${locale}/frequently-asked-questions`,
        name: t('SiteSocialNetwork/link-05'),
        target: '_blank',
      },
      contactUs: {
        href: `/${locale}/contact-us`,
        name: t('SiteSocialNetwork/link-04'),
      },
      partnership: {
        href: `/${locale}/partner-programs`,
      },
      login: {
        href: `${process.env.NEXT_PUBLIC_DASHBOARD_BASE_URL as string}/${locale}/login`,
        name: t('SiteNavbar/l-01'),
      },
      register: {
        href: `${process.env.NEXT_PUBLIC_DASHBOARD_BASE_URL as string}/${locale}/register`,
        name: `${t('SiteNavbar/l-18')}`,
      },
      Roadmap: {
        href: 'https://dashboard.louisebot.com/en-US/login',
        name: t('SiteSocialNetwork/link-06'),
        target: '_blank',
      },
      AIChatbot: {
        href: `/${locale}/ai-chatbot`,
        name: t('SiteNavbar/l-09'),
      },
      HumanClone: {
        href: `/${locale}/human-clone-page`,
        name: t('SiteNavbar/l-06'),
      },
      ScenarioChatbot: {
        href: `/${locale}/text-chatbot`,
        name: t('SiteNavbar/l-08'),
      },
      VideoBot: {
        href: `/${locale}/video-chatbot`,
        name: t('SiteNavbar/l-07'),
      },
      BecomeAnAffiliate: {
        name: t('AffiliationPrograms/becomeAffiliate'),
        href: `/${locale}/affiliation-programs`,
      },
      BecomeAPartner: {
        name: t('Agencies/agencies'),
        href: `/${locale}/partner-programs`,
      },
      ChatbotIntegration: {
        name: t('IntegrationsPage/title'),
        href: `/${locale}/chatbot-integration`,
      },
      TextVSVideo: {
        name: t('CompareTextAndVideo/t-01'),
        href: `/${locale}/compare-chatbot-video`,
      },
      WhyVideoBot: {
        name: t('CompareTextAndVideo/t-02'),
        href: `/${locale}/advantages-of-the-video-chatbot`,
      },
      IntegrationStripe: {
        href: `/${locale}/integration/Stripe`,
        name: t('Integration/Stripe/Title'),
      },
      SiteBot: {
        name: t('SiteNavbar/l-19'),
        href: `/${locale}/plug-and-play`,
      },
      Voicemail: {
        name: t('Voicemail/why_voicemail/title'),
        href: `/${locale}/features/voicemail`,
      },
      Marketplace: {
        name: t('Marketplace/title'),
        href: `/${locale}/marketplace`,
      },
    };
  }, [locale, t]);

  return { Links };
}
